import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Heading, SpinningLoader, styles, Text, TextLink, useModalControl } from '@frontend/design-system';
import { CopyLinkHelper } from '../../components/CopyLinkHelper';
import { WEAVE_HELP_URLS } from '../../constants';
import { useSchedulingLocationInfo } from '../../hooks';
import { useGetIsAppointmentTypeMappedWithPractitioner } from '../../hooks/booking-site/use-get-is-appointment-type-mapped-with-practitioner';
import { getBookingSiteUrl } from '../../utils';
import { EmptyAppointmentTypeMappingModal } from './EmptyAppointmentTypeMappingModal';
import { ProviderAppointmentTypeMappingInstructions } from './ProviderAppointmentTypeMappingInstructions';
import { bookingSiteSetupTrackingIds } from './trackingIds';

export const WebsiteTabContent = () => {
  const [modalLocationId, setModalLocationId] = useState<string>('');
  const { t } = useTranslation('scheduleGlobalSettings');
  const { modalProps, triggerProps } = useModalControl();
  const { isMultiLocation, selectedLocationIds, parentLocationId, getScopeName, getCFLocationMap } =
    useSchedulingLocationInfo();

  const {
    hasAnyLocationMapping,
    locationWiseMappingStatus,
    isLoading: isLoadingAppointmentTypeMapping,
  } = useGetIsAppointmentTypeMappedWithPractitioner();

  const bookingSiteUrls = useMemo(() => {
    if (isMultiLocation) {
      return {
        unifyLink: getBookingSiteUrl(parentLocationId),
        locationUrls: selectedLocationIds.reduce((acc, locationId) => {
          acc[locationId] = getBookingSiteUrl(locationId);
          return acc;
        }, {} as Record<string, string>),
      };
    }
    return {
      unifyLink: getBookingSiteUrl(selectedLocationIds[0]),
      locationUrls: {},
    };
  }, [isMultiLocation, parentLocationId, selectedLocationIds]);

  const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');

  const onlineSchedulingEnabledLocations = useMemo(() => {
    const onlineSchedulingCFEnabledLocationIds =
      Object.keys(onlineSchedulingCFLocationMap).filter((locationId) => !!onlineSchedulingCFLocationMap[locationId]) ||
      [];
    return onlineSchedulingCFEnabledLocationIds;
  }, [onlineSchedulingCFLocationMap]);

  const onlineSchedulingDisabledLocations = useMemo(() => {
    return selectedLocationIds.filter((locationId) => !onlineSchedulingEnabledLocations.includes(locationId));
  }, [selectedLocationIds, onlineSchedulingEnabledLocations]);

  const handleWarningIconClick = (locationId: string) => {
    setModalLocationId(locationId);
    triggerProps.onClick();
  };

  if (isLoadingAppointmentTypeMapping)
    return (
      <div css={[styles.flexCenter, { marginTop: theme.spacing(2) }]}>
        <SpinningLoader size='medium' />
      </div>
    );

  if (!hasAnyLocationMapping) {
    return <ProviderAppointmentTypeMappingInstructions css={{ marginTop: theme.spacing(2) }} />;
  }

  return (
    <>
      <Heading level={2}>{t('Booking Links')}</Heading>
      <Text>
        {t(
          'Add an appointment booking link to your website or social media pages. Find detailed instructions on setting up your booking link seamlessly across your digital platforms at'
        )}
      </Text>{' '}
      <TextLink
        trackingId={bookingSiteSetupTrackingIds.weaveHelpLink}
        size='large'
        to={WEAVE_HELP_URLS.ADD_ONLINE_SCHEDULING_LINK}
        target='_blank'
      >
        weavehelp.com
      </TextLink>
      {isMultiLocation ? (
        <>
          <section css={sectionStyle}>
            <Heading level={3}>{t('Multi-location booking site URL')}</Heading>
            <Text css={{ marginBottom: theme.spacing(1) }}>
              {t('Customers can view availability and schedule appointments at any of your office locations.')}
            </Text>
            <CopyLinkHelper
              textToCopy={bookingSiteUrls.unifyLink}
              trackingId={bookingSiteSetupTrackingIds.copyLinkButton}
            />
          </section>
          <section css={sectionStyle}>
            <Heading level={3}>{t('Single-location booking site URLs')}</Heading>
            <Text>{t('Customers can view availability and schedule appointments specifically at one location.')}</Text>
          </section>

          {onlineSchedulingEnabledLocations?.map((locationId: string) => {
            const hasMapping = locationWiseMappingStatus[locationId];
            const locationBookingUrl = bookingSiteUrls.locationUrls[locationId];

            return (
              <section key={locationId} css={multiContainerListStyle}>
                <Text size='large' css={multiHeadingListStyle}>
                  <Icon name='location' size={20} />
                  {getScopeName(locationId)}
                </Text>
                {hasMapping ? (
                  <CopyLinkHelper
                    textToCopy={locationBookingUrl}
                    trackingId={bookingSiteSetupTrackingIds.copyLinkButton}
                    hideButtonText
                  />
                ) : (
                  <Button
                    size='large'
                    variant='tertiary'
                    iconName='warning-badge'
                    label={t('Incomplete setup')}
                    onClick={() => handleWarningIconClick(locationId)}
                    css={{
                      padding: theme.spacing(0.5, 0.75),
                      height: 24,
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)', // Center the icon vertically
                      right: theme.spacing(2),
                      background: theme.colors.warning5,
                      color: theme.colors.warning70,
                      fontWeight: 'normal',
                      svg: { color: `${theme.colors.warning20} !important` }, // override hover color using `!important`
                      '&:hover': {
                        background: theme.colors.warning10,
                      },
                    }}
                  >
                    {t('Incomplete setup')}
                  </Button>
                )}
              </section>
            );
          })}
          {onlineSchedulingDisabledLocations.length > 0 && (
            <>
              <section css={[sectionStyle, { margin: theme.spacing(3, 0) }]}>
                <Heading level={3}>{t('Some locations don’t have Online Scheduling Enabled')}</Heading>
                <Trans t={t}>
                  <Text>
                    <TextLink
                      trackingId={bookingSiteSetupTrackingIds.contactSupportLink}
                      to={WEAVE_HELP_URLS.CONTACT_WEAVE_SUPPORT}
                      target='_blank'
                    >
                      Contact support
                    </TextLink>{' '}
                    to upgrade these locations and enable Online Scheduling
                  </Text>
                </Trans>
              </section>
              {onlineSchedulingDisabledLocations.map((locationId: string) => (
                <Text key={locationId} size='large' css={[multiHeadingListStyle, { margin: theme.spacing(2, 0) }]}>
                  <Icon name='location' size={20} />
                  {getScopeName(locationId)}
                </Text>
              ))}
            </>
          )}
          <EmptyAppointmentTypeMappingModal modalProps={modalProps} locationId={modalLocationId} />
        </>
      ) : (
        <section css={sectionStyle}>
          <Heading level={3}>{t("Your location's URL")}</Heading>
          <Text css={{ marginBottom: theme.spacing(1) }}>
            {t('Customers can view availability and schedule appointments with providers at your office.')}
          </Text>
          <CopyLinkHelper
            textToCopy={bookingSiteUrls.unifyLink}
            trackingId={bookingSiteSetupTrackingIds.copyLinkButton}
          />
        </section>
      )}
    </>
  );
};

const sectionStyle = css({
  marginTop: theme.spacing(2),
  paddingRight: theme.spacing(1),
});

const multiContainerListStyle = css({
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  marginTop: theme.spacing(2),
  height: 68,
});

const multiHeadingListStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontWeight: 'normal',
});
